import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  IconButton,
  Divider,
  Tabs,
  Tab,
  ButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { lt } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { ExhibitionDeletePasswordDialog } from './ExhibitionDeletePasswordDialog';
import { formatMonthYearLt } from '../utils/ltDateFormat';

const timeStringToDate = (timeStr: string): Date | null => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return new Date(2000, 0, 1, h, m, 0);
};

const dateToTimeString = (d: Date | null): string => {
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

interface Edukation {
  id: string;
  title: string;
  description: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location?: string;
  maxParticipants?: number;
  participantsConfirmedAt?: string | null;
  status: 'scheduled' | 'completed' | 'cancelled';
  isActive: boolean;
  notes?: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedColleague?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  assignedColleagues?: Array<{
    id: string;
    firstName: string;
    lastName: string;
  }>;
  watcherColleagues?: Array<{
    id: string;
    firstName: string;
    lastName: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface Colleague {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  department?: string;
}

interface EdukationFormData {
  title: string;
  description: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string;
  maxParticipants: number;
  notes: string;
  assignedColleagueIds: string[];
  watcherColleagueIds: string[];
}

interface EdukationTemplate {
  id: string;
  name: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
  maxParticipants: number;
  notes: string;
  /** Preferred; multiple default assignees (runners) */
  assignedColleagueIds?: string[];
  /** Watchers — schedule only, not in ataskaita */
  watcherColleagueIds?: string[];
  /** Legacy single assignee from older templates */
  assignedColleagueId?: string;
}

const EDUKATION_TEMPLATES_KEY = 'edukacija_templates_v1';
const MAX_EDUKATION_FORM_ASSIGNEES = 15;

function assigneeIdsFromEdukation(edukation: Edukation): string[] {
  if (edukation.assignedColleagues && edukation.assignedColleagues.length > 0) {
    return edukation.assignedColleagues.map((c) => c.id);
  }
  if (edukation.assignedColleague?.id) {
    return [edukation.assignedColleague.id];
  }
  return [''];
}

function watcherIdsFromEdukation(edukation: Edukation): string[] {
  if (edukation.watcherColleagues && edukation.watcherColleagues.length > 0) {
    return edukation.watcherColleagues.map((c) => c.id);
  }
  return [''];
}

function formatEdukationAssigneesLine(edukation: Edukation): string {
  if (edukation.assignedColleagues && edukation.assignedColleagues.length > 0) {
    return edukation.assignedColleagues.map((c) => `${c.firstName} ${c.lastName}`).join(', ');
  }
  if (edukation.assignedColleague) {
    return `${edukation.assignedColleague.firstName} ${edukation.assignedColleague.lastName}`;
  }
  return '';
}

function normalizeTemplateAssignees(tmpl: EdukationTemplate): EdukationTemplate {
  let ids: string[] = [];
  if (tmpl.assignedColleagueIds && tmpl.assignedColleagueIds.length > 0) {
    ids = [...tmpl.assignedColleagueIds];
  } else if (tmpl.assignedColleagueId) {
    ids = [tmpl.assignedColleagueId];
  }
  if (ids.length === 0) {
    ids = [''];
  }
  const watcherIds =
    tmpl.watcherColleagueIds && tmpl.watcherColleagueIds.length > 0
      ? [...tmpl.watcherColleagueIds]
      : [''];
  const { assignedColleagueId: _legacy, ...rest } = tmpl;
  return { ...rest, assignedColleagueIds: ids, watcherColleagueIds: watcherIds };
}

function isUserAssignedToEdukation(edukation: Edukation, userId?: string): boolean {
  if (!userId) return false;
  return assigneeIdsFromEdukation(edukation).includes(userId);
}

function compareEdukationsByDate(a: Edukation, b: Edukation): number {
  const dateCompare = a.eventDate.localeCompare(b.eventDate);
  if (dateCompare !== 0) return dateCompare;
  return a.startTime.localeCompare(b.startTime);
}

function eventDateYmd(edukation: Edukation): string {
  return String(edukation.eventDate || '').split('T')[0];
}

/** True if eventDate falls in the given calendar month. */
function matchesFilterMonth(edukation: Edukation, month: Date): boolean {
  const ymd = eventDateYmd(edukation);
  if (!ymd) return false;
  const from = format(startOfMonth(month), 'yyyy-MM-dd');
  const to = format(endOfMonth(month), 'yyyy-MM-dd');
  return ymd >= from && ymd <= to;
}

function isParticipantsConfirmed(edukation: Edukation): boolean {
  return Boolean(edukation.participantsConfirmedAt);
}

function getAssignedEdukationCardSx(assignedToMe: boolean, participantsConfirmed: boolean) {
  if (participantsConfirmed) {
    return {
      border: '2px solid',
      borderColor: 'success.main',
      backgroundColor: 'success.light',
      '& .MuiCardContent-root': { backgroundColor: 'success.light' }
    };
  }
  if (assignedToMe) {
    return {
      border: '2px solid',
      borderColor: 'error.main',
      backgroundColor: 'error.light',
      '& .MuiCardContent-root': { backgroundColor: 'error.light' }
    };
  }
  return undefined;
}

const Edukacijos: React.FC<{ embedded?: boolean; filterMonth?: Date }> = ({
  embedded = false,
  filterMonth: filterMonthProp
}) => {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const [edukations, setEdukations] = useState<Edukation[]>([]);
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEdukation, setEditingEdukation] = useState<Edukation | null>(null);
  const emptyFormData = (): EdukationFormData => ({
    title: '',
    description: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    location: '',
    maxParticipants: 0,
    notes: '',
    assignedColleagueIds: [''],
    watcherColleagueIds: ['']
  });
  const [formData, setFormData] = useState<EdukationFormData>(emptyFormData);
  const [activeTab, setActiveTab] = useState(0);
  const [filterMonthLocal, setFilterMonthLocal] = useState(() => startOfMonth(new Date()));
  const filterMonth = filterMonthProp ?? filterMonthLocal;
  const monthNavControlled = filterMonthProp != null;
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<EdukationTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState<EdukationTemplate>({
    id: '',
    name: '',
    title: '',
    description: '',
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    maxParticipants: 0,
    notes: '',
    assignedColleagueIds: [''],
    watcherColleagueIds: ['']
  });
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [participantDrafts, setParticipantDrafts] = useState<Record<string, string>>({});
  const [savingParticipantId, setSavingParticipantId] = useState<string | null>(null);
  const [participantsSuccess, setParticipantsSuccess] = useState<string | null>(null);
  const [unconfirmTargetId, setUnconfirmTargetId] = useState<string | null>(null);
  const [unconfirmingId, setUnconfirmingId] = useState<string | null>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const canManageEdukacijos = hasPermission('edukacijos.manage');
  const formReadOnly = !canManageEdukacijos;
  const minEventDateStr = format(new Date(), 'yyyy-MM-dd');

  const canUpdateParticipants = (edukation: Edukation): boolean => {
    if (edukation.status !== 'scheduled') return false;
    if (canManageEdukacijos) return true;
    if (!isUserAssignedToEdukation(edukation, user?.id)) return false;
    const eventYmd = edukation.eventDate.split('T')[0];
    return eventYmd >= minEventDateStr;
  };

  const getParticipantDraft = (edukation: Edukation): string =>
    participantDrafts[edukation.id] ?? String(edukation.maxParticipants ?? '');

  const canConfirmParticipants = (edukation: Edukation, draft: string): boolean => {
    if (savingParticipantId === edukation.id) return false;
    if (!isParticipantsConfirmed(edukation)) return true;
    return draft !== String(edukation.maxParticipants ?? '');
  };

  const handleSaveParticipants = async (edukationId: string) => {
    const edukation = edukations.find((e) => e.id === edukationId);
    const draft = participantDrafts[edukationId] ?? String(edukation?.maxParticipants ?? '');
    const maxParticipants = parseInt(draft, 10);
    if (!draft || Number.isNaN(maxParticipants) || maxParticipants < 1) {
      setError(t('edukacijos.participantsInvalid', 'Enter a valid participant count (at least 1)'));
      return;
    }

    setSavingParticipantId(edukationId);
    setParticipantsSuccess(null);
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.EDUKATION_PARTICIPANTS(edukationId)), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ maxParticipants })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: '' }));
        throw new Error(
          errorData.error || t('edukacijos.participantsUpdateFailed', 'Failed to update participant count')
        );
      }

      const data = await response.json().catch(() => ({}));
      if (data.edukation) {
        setEdukations((prev) =>
          prev.map((e) => (e.id === edukationId ? { ...e, ...data.edukation } : e))
        );
      }

      setParticipantDrafts((prev) => {
        const next = { ...prev };
        delete next[edukationId];
        return next;
      });
      setParticipantsSuccess(t('edukacijos.participantsSaved', 'Participant count updated'));
      await fetchEdukations();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('edukacijos.participantsUpdateFailed', 'Failed to update participant count')
      );
    } finally {
      setSavingParticipantId(null);
    }
  };

  const handleUnconfirmParticipants = async (edukationId: string) => {
    setUnconfirmingId(edukationId);
    setParticipantsSuccess(null);
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.EDUKATION_PARTICIPANTS_UNCONFIRM(edukationId)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: '{}'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: '' }));
        throw new Error(
          errorData.error ||
            t('edukacijos.participantsUnconfirmFailed', 'Nepavyko atšaukti patvirtinimo')
        );
      }
      const data = await response.json().catch(() => ({}));
      if (data.edukation) {
        setEdukations((prev) =>
          prev.map((e) => (e.id === edukationId ? { ...e, ...data.edukation } : e))
        );
      }
      setUnconfirmTargetId(null);
      setParticipantsSuccess(
        t(
          'edukacijos.participantsUnconfirmed',
          'Patvirtinimas atšauktas — skaičių galite patvirtinti vėliau'
        )
      );
      await fetchEdukations();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('edukacijos.participantsUnconfirmFailed', 'Nepavyko atšaukti patvirtinimo')
      );
    } finally {
      setUnconfirmingId(null);
    }
  };

  const formColleagueSlotCount = (prev: {
    assignedColleagueIds: string[];
    watcherColleagueIds: string[];
  }) =>
    (prev.assignedColleagueIds?.length || 0) + (prev.watcherColleagueIds?.length || 0);

  const setFormAssigneeAt = (index: number, value: string) => {
    setFormData((prev) => {
      const next = [...prev.assignedColleagueIds];
      next[index] = value;
      return { ...prev, assignedColleagueIds: next };
    });
  };

  const addFormAssigneeRow = () => {
    setFormData((prev) => {
      if (formColleagueSlotCount(prev) >= MAX_EDUKATION_FORM_ASSIGNEES) return prev;
      return { ...prev, assignedColleagueIds: [...prev.assignedColleagueIds, ''] };
    });
  };

  const removeFormAssigneeRow = (index: number) => {
    setFormData((prev) => {
      if (prev.assignedColleagueIds.length <= 1) return prev;
      const next = prev.assignedColleagueIds.filter((_, i) => i !== index);
      return { ...prev, assignedColleagueIds: next.length ? next : [''] };
    });
  };

  const setFormWatcherAt = (index: number, value: string) => {
    setFormData((prev) => {
      const next = [...prev.watcherColleagueIds];
      next[index] = value;
      return { ...prev, watcherColleagueIds: next };
    });
  };

  const addFormWatcherRow = () => {
    setFormData((prev) => {
      if (formColleagueSlotCount(prev) >= MAX_EDUKATION_FORM_ASSIGNEES) return prev;
      return { ...prev, watcherColleagueIds: [...prev.watcherColleagueIds, ''] };
    });
  };

  const removeFormWatcherRow = (index: number) => {
    setFormData((prev) => {
      if (prev.watcherColleagueIds.length <= 1) return prev;
      const next = prev.watcherColleagueIds.filter((_, i) => i !== index);
      return { ...prev, watcherColleagueIds: next.length ? next : [''] };
    });
  };

  const setTemplateAssigneeAt = (index: number, value: string) => {
    setTemplateForm((prev) => {
      const ids = [...(prev.assignedColleagueIds || [''])];
      ids[index] = value;
      return { ...prev, assignedColleagueIds: ids };
    });
  };

  const addTemplateAssigneeRow = () => {
    setTemplateForm((prev) => {
      const ids = [...(prev.assignedColleagueIds || [''])];
      const watchers = prev.watcherColleagueIds || [''];
      if (ids.length + watchers.length >= MAX_EDUKATION_FORM_ASSIGNEES) return prev;
      return { ...prev, assignedColleagueIds: [...ids, ''] };
    });
  };

  const removeTemplateAssigneeRow = (index: number) => {
    setTemplateForm((prev) => {
      const ids = [...(prev.assignedColleagueIds || [''])];
      if (ids.length <= 1) return prev;
      const next = ids.filter((_, i) => i !== index);
      return { ...prev, assignedColleagueIds: next.length ? next : [''] };
    });
  };

  const setTemplateWatcherAt = (index: number, value: string) => {
    setTemplateForm((prev) => {
      const ids = [...(prev.watcherColleagueIds || [''])];
      ids[index] = value;
      return { ...prev, watcherColleagueIds: ids };
    });
  };

  const addTemplateWatcherRow = () => {
    setTemplateForm((prev) => {
      const assignees = prev.assignedColleagueIds || [''];
      const ids = [...(prev.watcherColleagueIds || [''])];
      if (assignees.length + ids.length >= MAX_EDUKATION_FORM_ASSIGNEES) return prev;
      return { ...prev, watcherColleagueIds: [...ids, ''] };
    });
  };

  const removeTemplateWatcherRow = (index: number) => {
    setTemplateForm((prev) => {
      const ids = [...(prev.watcherColleagueIds || [''])];
      if (ids.length <= 1) return prev;
      const next = ids.filter((_, i) => i !== index);
      return { ...prev, watcherColleagueIds: next.length ? next : [''] };
    });
  };

  const upcomingEdukations = useMemo(() => {
    const upcoming = edukations.filter(
      (edukation) =>
        edukation.status === 'scheduled' &&
        edukation.isActive &&
        matchesFilterMonth(edukation, filterMonth)
    );
    const mine: Edukation[] = [];
    const others: Edukation[] = [];
    for (const edukation of upcoming) {
      if (isUserAssignedToEdukation(edukation, user?.id)) {
        mine.push(edukation);
      } else {
        others.push(edukation);
      }
    }
    mine.sort((a, b) => {
      const aConfirmed = isParticipantsConfirmed(a);
      const bConfirmed = isParticipantsConfirmed(b);
      if (aConfirmed !== bConfirmed) {
        return aConfirmed ? 1 : -1;
      }
      return compareEdukationsByDate(a, b);
    });
    others.sort(compareEdukationsByDate);
    return [...mine, ...others];
  }, [edukations, user?.id, filterMonth]);

  const completedEdukations = useMemo(
    () =>
      edukations.filter(
        (edukation) =>
          edukation.status === 'completed' &&
          edukation.isActive &&
          matchesFilterMonth(edukation, filterMonth)
      ),
    [edukations, filterMonth]
  );

  const cancelledEdukations = useMemo(
    () =>
      edukations.filter(
        (edukation) =>
          edukation.status === 'cancelled' &&
          edukation.isActive &&
          matchesFilterMonth(edukation, filterMonth)
      ),
    [edukations, filterMonth]
  );

  const fetchEdukations = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.EDUKATIONS), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch edukations');
      }

      const data = await response.json();
      setEdukations(data.edukations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch edukations');
    } finally {
      setLoading(false);
    }
  };

  const fetchColleagues = async () => {
    if (!canManageEdukacijos) return;

    try {
      const response = await fetch(getApiUrl(`${API_ENDPOINTS.EDUKATIONS}/colleagues/available`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to fetch colleagues');
      }

      const data = await response.json();
      setColleagues(data.colleagues || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch colleagues');
    }
  };

  useEffect(() => {
    fetchEdukations();
    fetchColleagues();
  }, [canManageEdukacijos]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(EDUKATION_TEMPLATES_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as EdukationTemplate[];
      if (Array.isArray(parsed)) {
        setTemplates(parsed.map((t) => normalizeTemplateAssignees(t as EdukationTemplate)));
      }
    } catch (e) {
      console.error('Failed to load edukation templates', e);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(EDUKATION_TEMPLATES_KEY, JSON.stringify(templates));
  }, [templates]);

  const handleOpenDialog = (edukation?: Edukation) => {
    setDialogError(null);
    setSelectedTemplateId('');
    if (edukation) {
      setEditingEdukation(edukation);
      setFormData({
        title: edukation.title,
        description: edukation.description,
        eventDate: edukation.eventDate.split('T')[0],
        startTime: edukation.startTime,
        endTime: edukation.endTime,
        location: edukation.location || '',
        maxParticipants: edukation.maxParticipants || 0,
        notes: edukation.notes || '',
        assignedColleagueIds: assigneeIdsFromEdukation(edukation),
        watcherColleagueIds: watcherIdsFromEdukation(edukation)
      });
    } else {
      setEditingEdukation(null);
      setFormData(emptyFormData());
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEdukation(null);
    setDialogError(null);
    setFormData(emptyFormData());
    setSelectedTemplateId('');
  };

  const openDialogLoadError = (message: string) => {
    setEditingEdukation(null);
    setSelectedTemplateId('');
    setFormData(emptyFormData());
    setDialogError(message);
    setDialogOpen(true);
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        id?: string;
        date?: string;
        edukation?: Edukation | null;
      }>;
      const detail = customEvent.detail || {};

      if (detail.edukation) {
        handleOpenDialog(detail.edukation);
        return;
      }

      if (detail.id) {
        const existing = edukations.find((e) => e.id === detail.id);
        if (existing) {
          handleOpenDialog(existing);
          return;
        }
        void (async () => {
          try {
            const res = await fetch(getApiUrl(`${API_ENDPOINTS.EDUKATIONS}/${detail.id}`), {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
              openDialogLoadError(
                (payload as { error?: string }).error || t('edukacijos.loadFailed', 'Could not load edukation')
              );
              return;
            }
            const ed = (payload as { edukation?: Edukation }).edukation;
            if (ed) {
              handleOpenDialog(ed);
            }
          } catch (e) {
            openDialogLoadError(
              e instanceof Error ? e.message : t('edukacijos.loadFailed', 'Could not load edukation')
            );
          }
        })();
        return;
      }

      if (detail.date) {
        if (!canManageEdukacijos) {
          return;
        }
        handleOpenDialog();
        setFormData((prev) => ({ ...prev, eventDate: detail.date || '' }));
      }
    };

    window.addEventListener('exhibition-calendar-open-edukation', handler as EventListener);
    return () => window.removeEventListener('exhibition-calendar-open-edukation', handler as EventListener);
  }, [edukations, canManageEdukacijos, t]);

  const applyTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    setFormData((prev) => ({
      ...prev,
      title: template.title,
      description: template.description,
      startTime: template.startTime,
      endTime: template.endTime,
      location: template.location,
      maxParticipants: template.maxParticipants,
      notes: template.notes,
      assignedColleagueIds:
        template.assignedColleagueIds && template.assignedColleagueIds.length > 0
          ? [...template.assignedColleagueIds]
          : [''],
      watcherColleagueIds:
        template.watcherColleagueIds && template.watcherColleagueIds.length > 0
          ? [...template.watcherColleagueIds]
          : ['']
    }));
  };

  const resetTemplateForm = () => {
    setEditingTemplateId(null);
    setTemplateForm({
      id: '',
      name: '',
      title: '',
      description: '',
      startTime: '09:00',
      endTime: '10:00',
      location: '',
      maxParticipants: 0,
      notes: '',
      assignedColleagueIds: [''],
      watcherColleagueIds: ['']
    });
  };

  const saveTemplate = () => {
    if (!templateForm.name.trim() || !templateForm.title.trim()) {
      return;
    }

    if (editingTemplateId) {
      setTemplates((prev) =>
        prev.map((tmpl) =>
          tmpl.id === editingTemplateId
            ? normalizeTemplateAssignees({ ...templateForm, id: editingTemplateId })
            : tmpl
        )
      );
    } else {
      const id = crypto.randomUUID();
      setTemplates((prev) => [...prev, normalizeTemplateAssignees({ ...templateForm, id })]);
    }

    resetTemplateForm();
  };

  const editTemplate = (template: EdukationTemplate) => {
    setEditingTemplateId(template.id);
    const normalized = normalizeTemplateAssignees(template);
    setTemplateForm(normalized);
  };

  const deleteTemplate = (templateId: string) => {
    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
    if (selectedTemplateId === templateId) {
      setSelectedTemplateId('');
    }
    if (editingTemplateId === templateId) {
      resetTemplateForm();
    }
  };

  const handleSubmit = async () => {
    if (!canManageEdukacijos) {
      return;
    }
    try {
      const endpoint = editingEdukation
        ? `${API_ENDPOINTS.EDUKATIONS}/${editingEdukation.id}`
        : API_ENDPOINTS.EDUKATIONS;

      const method = editingEdukation ? 'PUT' : 'POST';

      const payload = {
        title: formData.title,
        description: formData.description,
        eventDate: formData.eventDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: formData.location,
        maxParticipants: formData.maxParticipants,
        notes: formData.notes,
        assignedColleagueIds: formData.assignedColleagueIds.filter(Boolean),
        watcherColleagueIds: formData.watcherColleagueIds.filter(Boolean)
      };

      const response = await fetch(getApiUrl(endpoint), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save edukation');
      }

      handleCloseDialog();
      fetchEdukations();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save edukation');
    }
  };

  const requestDeleteEdukation = (edukationId: string) => {
    setDeleteTargetId(edukationId);
  };

  const performDeleteEdukation = async (edukationId: string) => {
    try {
      const response = await fetch(getApiUrl(`${API_ENDPOINTS.EDUKATIONS}/${edukationId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete edukation');
      }

      setDeleteTargetId(null);
      fetchEdukations();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete edukation');
      throw err;
    }
  };

  const handleStatusChange = async (edukationId: string, newStatus: string) => {
    try {
      const response = await fetch(getApiUrl(`${API_ENDPOINTS.EDUKATIONS}/${edukationId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as { error?: string; code?: string }));
        if (errorData.code === 'PARTICIPANTS_NOT_CONFIRMED') {
          throw new Error(
            t(
              'edukacijos.cannotCompleteUnconfirmed',
              'Cannot mark as completed until the participant count is confirmed'
            )
          );
        }
        throw new Error(
          errorData.error || t('edukacijos.failedToUpdateStatus', 'Failed to update status')
        );
      }

      await fetchEdukations();
      window.dispatchEvent(new Event('exhibition-calendar-refresh'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('edukacijos.failedToUpdateStatus', 'Failed to update status'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled': return t('edukacijos.status.scheduled', 'Scheduled');
      case 'completed': return t('edukacijos.status.completed', 'Completed');
      case 'cancelled': return t('edukacijos.status.cancelled', 'Cancelled');
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {!embedded ? (
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
            <SchoolIcon />
            {t('edukacijos.title', 'Edukacijos')}
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1 }} />
        )}

        {canManageEdukacijos && (
          <Tooltip title={t('edukacijos.addNew', 'Add new edukation')}>
            <Fab
              color="primary"
              size="small"
              aria-label="add edukation"
              onClick={() => handleOpenDialog()}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {participantsSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setParticipantsSuccess(null)}>
          {participantsSuccess}
        </Alert>
      )}

      {!monthNavControlled && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            mb: 2
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', textAlign: 'center' }}>
            {formatMonthYearLt(filterMonth)}
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={() => setFilterMonthLocal((m) => startOfMonth(subMonths(m, 1)))}>
              {t('edukacijos.monthPrev', 'Atgal')}
            </Button>
            <Button onClick={() => setFilterMonthLocal(startOfMonth(new Date()))}>
              {t('edukacijos.thisMonth', 'Šis mėnuo')}
            </Button>
            <Button onClick={() => setFilterMonthLocal((m) => startOfMonth(addMonths(m, 1)))}>
              {t('edukacijos.monthNext', 'Pirmyn')}
            </Button>
          </ButtonGroup>
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="edukacijos tabs"
          sx={{
            '& .MuiTab-root': { minHeight: 48, fontSize: '1rem', fontWeight: 500, textTransform: 'none' },
            '& .Mui-selected': { color: 'primary.main', fontWeight: 600 }
          }}
        >
          <Tab
            label={`${t('edukacijos.upcoming', 'Upcoming Edukacijos')} (${upcomingEdukations.length})`}
            id="edukacijos-tab-0"
            aria-controls="edukacijos-tabpanel-0"
          />
          <Tab
            label={`${t('edukacijos.completed', 'Completed Edukacijos')} (${completedEdukations.length})`}
            id="edukacijos-tab-1"
            aria-controls="edukacijos-tabpanel-1"
          />
          {canManageEdukacijos && (
            <Tab
              label={`${t('edukacijos.cancelled', 'Atšauktos edukacijos')} (${cancelledEdukations.length})`}
              id="edukacijos-tab-2"
              aria-controls="edukacijos-tabpanel-2"
            />
          )}
        </Tabs>
      </Box>

      <div
        role="tabpanel"
        hidden={activeTab !== 0}
        id="edukacijos-tabpanel-0"
        aria-labelledby="edukacijos-tab-0"
        style={{ transition: 'opacity 0.3s ease-in-out', opacity: activeTab === 0 ? 1 : 0 }}
      >
        {activeTab === 0 && (
          <>
            {upcomingEdukations.length > 0 ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
                {upcomingEdukations.map((edukation) => {
                  const assignedToMe = isUserAssignedToEdukation(edukation, user?.id);
                  const participantsConfirmed = isParticipantsConfirmed(edukation);
                  const participantDraft = getParticipantDraft(edukation);
                  const confirmEnabled = canConfirmParticipants(edukation, participantDraft);

                  return (
                  <Box key={edukation.id}>
                    <Card sx={getAssignedEdukationCardSx(assignedToMe, participantsConfirmed)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                          <Typography variant="h6" component="h3">{edukation.title}</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                            {participantsConfirmed ? (
                              <Chip
                                label={t('edukacijos.participantsConfirmed', 'Count confirmed')}
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label={t('edukacijos.needsConfirmation', 'Needs confirmation')}
                                color="warning"
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {assignedToMe && !participantsConfirmed && (
                              <Chip
                                label={t('edukacijos.mySession', 'My session')}
                                color="error"
                                size="small"
                                variant="outlined"
                              />
                            )}
                            <Chip label={getStatusLabel(edukation.status)} color={getStatusColor(edukation.status)} size="small" />
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                        >
                          {edukation.description}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            📅 {formatDate(edukation.eventDate)} {formatTime(edukation.startTime)} - {formatTime(edukation.endTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">📍 {edukation.location}</Typography>
                          {canUpdateParticipants(edukation) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                👥 {t('edukacijos.maxParticipantsLabel', 'Max participants')}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={participantDraft}
                                  onChange={(e) =>
                                    setParticipantDrafts((prev) => ({
                                      ...prev,
                                      [edukation.id]: e.target.value
                                    }))
                                  }
                                  inputProps={{ min: 1 }}
                                  sx={{ width: 100 }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!confirmEnabled}
                                  onClick={() => handleSaveParticipants(edukation.id)}
                                >
                                  {savingParticipantId === edukation.id ? (
                                    <CircularProgress size={18} color="inherit" />
                                  ) : (
                                    t('edukacijos.confirmParticipants', 'Confirm')
                                  )}
                                </Button>
                                {participantsConfirmed && canManageEdukacijos && (
                                  <Button
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    disabled={unconfirmingId === edukation.id}
                                    onClick={() => setUnconfirmTargetId(edukation.id)}
                                  >
                                    {unconfirmingId === edukation.id ? (
                                      <CircularProgress size={18} color="inherit" />
                                    ) : (
                                      t('edukacijos.unconfirmParticipants', 'Atšaukti patvirtinimą')
                                    )}
                                  </Button>
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {participantsConfirmed &&
                                participantDraft === String(edukation.maxParticipants ?? '')
                                  ? t(
                                      'edukacijos.participantsConfirmedHint',
                                      'Participant count is confirmed'
                                    )
                                  : t(
                                      'edukacijos.participantsNeedsConfirmHint',
                                      'Confirm the actual participant count before the session'
                                    )}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              👥 {t('edukacijos.maxParticipantsLabel', 'Max participants')}: {edukation.maxParticipants}
                            </Typography>
                          )}
                          {formatEdukationAssigneesLine(edukation) && (
                            <Typography variant="caption" color="text.secondary">
                              👨‍💼 {t('edukacijos.assignedLabel', 'Assigned')}: {formatEdukationAssigneesLine(edukation)}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                      {canManageEdukacijos && (
                        <>
                          <Divider />
                          <CardActions sx={{ justifyContent: 'space-between' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>{t('edukacijos.statusLabel', 'Status')}</InputLabel>
                              <Select value={edukation.status} label={t('edukacijos.statusLabel', 'Status')} onChange={(e) => handleStatusChange(edukation.id, e.target.value)}>
                                <MenuItem value="scheduled">{t('edukacijos.status.scheduled', 'Scheduled')}</MenuItem>
                                <MenuItem value="completed">{t('edukacijos.status.completed', 'Completed')}</MenuItem>
                                <MenuItem value="cancelled">{t('edukacijos.status.cancelled', 'Cancelled')}</MenuItem>
                              </Select>
                            </FormControl>
                            <Box>
                              <IconButton size="small" onClick={() => handleOpenDialog(edukation)}><EditIcon /></IconButton>
                              <IconButton size="small" color="error" onClick={() => requestDeleteEdukation(edukation.id)}><DeleteIcon /></IconButton>
                            </Box>
                          </CardActions>
                        </>
                      )}
                    </Card>
                  </Box>
                  );
                })}
              </Box>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body1" color="text.secondary" align="center">
                    {t('edukacijos.noUpcoming', 'No upcoming edukacijos scheduled')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <div
        role="tabpanel"
        hidden={activeTab !== 1}
        id="edukacijos-tabpanel-1"
        aria-labelledby="edukacijos-tab-1"
        style={{ transition: 'opacity 0.3s ease-in-out', opacity: activeTab === 1 ? 1 : 0 }}
      >
        {activeTab === 1 && (
          <>
            {completedEdukations.length > 0 ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                {completedEdukations.map((edukation) => (
                  <Box key={edukation.id}>
                    <Card sx={{ border: '2px solid', borderColor: 'success.main', backgroundColor: 'success.light', '& .MuiCardContent-root': { backgroundColor: 'success.light' } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" component="h3">{edukation.title}</Typography>
                          <Chip label={getStatusLabel(edukation.status)} color={getStatusColor(edukation.status)} size="small" />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                        >
                          {edukation.description}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            📅 {formatDate(edukation.eventDate)} {formatTime(edukation.startTime)} - {formatTime(edukation.endTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">📍 {edukation.location}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            👥 {t('edukacijos.maxParticipantsLabel', 'Max participants')}: {edukation.maxParticipants}
                          </Typography>
                          {formatEdukationAssigneesLine(edukation) && (
                            <Typography variant="caption" color="text.secondary">
                              👨‍💼 {t('edukacijos.assignedLabel', 'Assigned')}: {formatEdukationAssigneesLine(edukation)}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                      {canManageEdukacijos && (
                        <>
                          <Divider />
                          <CardActions sx={{ justifyContent: 'space-between' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>{t('edukacijos.statusLabel', 'Status')}</InputLabel>
                              <Select value={edukation.status} label={t('edukacijos.statusLabel', 'Status')} onChange={(e) => handleStatusChange(edukation.id, e.target.value)}>
                                <MenuItem value="scheduled">{t('edukacijos.status.scheduled', 'Scheduled')}</MenuItem>
                                <MenuItem value="completed">{t('edukacijos.status.completed', 'Completed')}</MenuItem>
                                <MenuItem value="cancelled">{t('edukacijos.status.cancelled', 'Cancelled')}</MenuItem>
                              </Select>
                            </FormControl>
                            <Box>
                              <IconButton size="small" onClick={() => handleOpenDialog(edukation)}><EditIcon /></IconButton>
                              <IconButton size="small" color="error" onClick={() => requestDeleteEdukation(edukation.id)}><DeleteIcon /></IconButton>
                            </Box>
                          </CardActions>
                        </>
                      )}
                    </Card>
                  </Box>
                ))}
              </Box>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body1" color="text.secondary" align="center">
                    {t('edukacijos.noCompleted', 'No completed edukacijos yet')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Tab Panel 2: Cancelled Edukacijos (managers only) */}
      {canManageEdukacijos && (
        <div
          role="tabpanel"
          hidden={activeTab !== 2}
          id="edukacijos-tabpanel-2"
          aria-labelledby="edukacijos-tab-2"
          style={{ transition: 'opacity 0.3s ease-in-out', opacity: activeTab === 2 ? 1 : 0 }}
        >
          {activeTab === 2 && (
            <>
              {cancelledEdukations.length > 0 ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                  {cancelledEdukations.map((edukation) => (
                    <Box key={edukation.id}>
                      <Card
                        sx={{
                          border: '2px solid',
                          borderColor: 'warning.main',
                          backgroundColor: 'warning.light',
                          '& .MuiCardContent-root': { backgroundColor: 'warning.light' }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="h6" component="h3">{edukation.title}</Typography>
                            <Chip label={getStatusLabel(edukation.status)} color={getStatusColor(edukation.status)} size="small" />
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                          >
                            {edukation.description}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              📅 {formatDate(edukation.eventDate)} {formatTime(edukation.startTime)} - {formatTime(edukation.endTime)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">📍 {edukation.location}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              👥 {t('edukacijos.maxParticipantsLabel', 'Max participants')}: {edukation.maxParticipants}
                            </Typography>
                            {formatEdukationAssigneesLine(edukation) && (
                              <Typography variant="caption" color="text.secondary">
                                👨‍💼 {t('edukacijos.assignedLabel', 'Assigned')}: {formatEdukationAssigneesLine(edukation)}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                        {canManageEdukacijos && (
                          <>
                            <Divider />
                            <CardActions sx={{ justifyContent: 'space-between' }}>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>{t('edukacijos.statusLabel', 'Status')}</InputLabel>
                                <Select value={edukation.status} label={t('edukacijos.statusLabel', 'Status')} onChange={(e) => handleStatusChange(edukation.id, e.target.value)}>
                                  <MenuItem value="scheduled">{t('edukacijos.status.scheduled', 'Scheduled')}</MenuItem>
                                  <MenuItem value="completed">{t('edukacijos.status.completed', 'Completed')}</MenuItem>
                                  <MenuItem value="cancelled">{t('edukacijos.status.cancelled', 'Cancelled')}</MenuItem>
                                </Select>
                              </FormControl>
                              <Box>
                                <IconButton size="small" onClick={() => handleOpenDialog(edukation)}><EditIcon /></IconButton>
                                <IconButton size="small" color="error" onClick={() => requestDeleteEdukation(edukation.id)}><DeleteIcon /></IconButton>
                              </Box>
                            </CardActions>
                          </>
                        )}
                      </Card>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Card>
                  <CardContent>
                    <Typography variant="body1" color="text.secondary" align="center">
                      {t('edukacijos.noCancelled', 'No cancelled edukacijos')}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {formReadOnly
              ? t('edukacijos.viewTitle', 'Edukation')
              : editingEdukation
                ? t('edukacijos.edit', 'Edit Edukation')
                : t('edukacijos.addNew', 'Add New Edukation')}
          </DialogTitle>
          <DialogContent>
            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {canManageEdukacijos && !editingEdukation && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t('edukacijos.template', 'Template')}</InputLabel>
                    <Select
                      value={selectedTemplateId}
                      label={t('edukacijos.template', 'Template')}
                      onChange={(e) => applyTemplate(e.target.value)}
                    >
                      <MenuItem value="">{t('edukacijos.noTemplate', 'No template')}</MenuItem>
                      {templates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={() => setTemplatesDialogOpen(true)}>
                    {t('edukacijos.manageTemplates', 'Manage templates')}
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                <TextField fullWidth label={t('edukacijos.titleLabel', 'Title')} value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required disabled={formReadOnly} />
                <TextField
                  fullWidth
                  label={t('edukacijos.description', 'Description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('edukacijos.descriptionPlaceholder', 'Write a description (multiple lines supported)…')}
                  multiline
                  minRows={3}
                  maxRows={10}
                  sx={{
                    '& .MuiInputBase-inputMultiline': {
                      lineHeight: 1.5
                    }
                  }}
                  helperText={t('edukacijos.multilineHint', 'Tip: use Enter for new lines')}
                  required
                  disabled={formReadOnly}
                />
                <TextField
                  fullWidth
                  label={t('edukacijos.eventDate', 'Event Date')}
                  type="date"
                  value={formData.eventDate}
                  onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={formReadOnly}
                  inputProps={{ min: !editingEdukation ? minEventDateStr : undefined }}
                />
                <TimePicker label={t('edukacijos.startTime', 'Start Time')} value={timeStringToDate(formData.startTime)} onChange={(time) => setFormData({ ...formData, startTime: dateToTimeString(time) })} timeSteps={{ minutes: 15 }} minTime={new Date(2000, 0, 1, 9, 0, 0)} maxTime={new Date(2000, 0, 1, 21, 0, 0)} skipDisabled disabled={formReadOnly} slotProps={{ textField: { fullWidth: true, required: true } }} />
                <TimePicker label={t('edukacijos.endTime', 'End Time')} value={timeStringToDate(formData.endTime)} onChange={(time) => setFormData({ ...formData, endTime: dateToTimeString(time) })} timeSteps={{ minutes: 15 }} minTime={new Date(2000, 0, 1, 9, 0, 0)} maxTime={new Date(2000, 0, 1, 21, 0, 0)} skipDisabled disabled={formReadOnly} slotProps={{ textField: { fullWidth: true, required: true } }} />
                <TextField fullWidth label={t('edukacijos.location', 'Location')} value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required disabled={formReadOnly} />
                <TextField fullWidth label={t('edukacijos.maxParticipants', 'Max Participants')} type="number" value={formData.maxParticipants} onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) })} inputProps={{ min: 1 }} required disabled={formReadOnly} />
                <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('edukacijos.assignColleaguesHeading', 'Priskirti kolegos')}
                  </Typography>
                  {formData.assignedColleagueIds.map((colleagueId, assigneeIndex) => (
                    <Box key={assigneeIndex} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                      <FormControl fullWidth disabled={formReadOnly}>
                        <InputLabel id={`eduk-assign-${assigneeIndex}`}>
                          {t('edukacijos.assignColleague', 'Assign Colleague')} {assigneeIndex + 1}
                        </InputLabel>
                        <Select
                          labelId={`eduk-assign-${assigneeIndex}`}
                          value={colleagueId}
                          onChange={(e) => setFormAssigneeAt(assigneeIndex, e.target.value)}
                          label={`${t('edukacijos.assignColleague', 'Assign Colleague')} ${assigneeIndex + 1}`}
                        >
                          <MenuItem value="">
                            <em>{t('edukacijos.noAssignment', 'No assignment')}</em>
                          </MenuItem>
                          {colleagues.map((colleague) => (
                            <MenuItem key={colleague.id} value={colleague.id}>
                              {colleague.firstName} {colleague.lastName} {colleague.department && ` (${colleague.department})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {!formReadOnly && formData.assignedColleagueIds.length > 1 && (
                        <Tooltip title={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}>
                          <IconButton
                            aria-label={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}
                            onClick={() => removeFormAssigneeRow(assigneeIndex)}
                            sx={{ mt: 0.5 }}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                  {!formReadOnly &&
                    formColleagueSlotCount(formData) < MAX_EDUKATION_FORM_ASSIGNEES && (
                    <Tooltip title={t('edukacijos.addAnotherColleague', 'Add another colleague')}>
                      <IconButton
                        color="primary"
                        aria-label={t('edukacijos.addAnotherColleague', 'Add another colleague')}
                        onClick={addFormAssigneeRow}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {t('edukacijos.watchersHeading', 'Stebėtojai')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t(
                      'edukacijos.watchersHint',
                      'Matomi darbo grafike, bet neįtraukiami į veiklos ataskaitą.'
                    )}
                  </Typography>
                  {formData.watcherColleagueIds.map((colleagueId, watcherIndex) => (
                    <Box key={watcherIndex} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                      <FormControl fullWidth disabled={formReadOnly}>
                        <InputLabel id={`eduk-watch-${watcherIndex}`}>
                          {t('edukacijos.assignWatcher', 'Stebėtojas')} {watcherIndex + 1}
                        </InputLabel>
                        <Select
                          labelId={`eduk-watch-${watcherIndex}`}
                          value={colleagueId}
                          onChange={(e) => setFormWatcherAt(watcherIndex, e.target.value)}
                          label={`${t('edukacijos.assignWatcher', 'Stebėtojas')} ${watcherIndex + 1}`}
                        >
                          <MenuItem value="">
                            <em>{t('edukacijos.noAssignment', 'No assignment')}</em>
                          </MenuItem>
                          {colleagues.map((colleague) => (
                            <MenuItem key={colleague.id} value={colleague.id}>
                              {colleague.firstName} {colleague.lastName}{' '}
                              {colleague.department && ` (${colleague.department})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {!formReadOnly && formData.watcherColleagueIds.length > 1 && (
                        <Tooltip title={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}>
                          <IconButton
                            aria-label={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}
                            onClick={() => removeFormWatcherRow(watcherIndex)}
                            sx={{ mt: 0.5 }}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                  {!formReadOnly &&
                    formColleagueSlotCount(formData) < MAX_EDUKATION_FORM_ASSIGNEES && (
                    <Tooltip title={t('edukacijos.addAnotherWatcher', 'Pridėti stebėtoją')}>
                      <IconButton
                        color="primary"
                        aria-label={t('edukacijos.addAnotherWatcher', 'Pridėti stebėtoją')}
                        onClick={addFormWatcherRow}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                  <TextField
                    fullWidth
                    label={t('edukacijos.notes', 'Notes')}
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder={t('edukacijos.notesPlaceholder', 'Optional notes…')}
                    multiline
                    minRows={2}
                    maxRows={8}
                    sx={{
                      '& .MuiInputBase-inputMultiline': {
                        lineHeight: 1.5
                      }
                    }}
                    disabled={formReadOnly}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {formReadOnly ? t('common.close', 'Close') : t('edukacijos.cancel', 'Cancel')}
            </Button>
            {canManageEdukacijos && (
              <Button onClick={handleSubmit} variant="contained">
                {editingEdukation ? t('edukacijos.update', 'Update') : t('edukacijos.create', 'Create')}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      <Dialog open={templatesDialogOpen} onClose={() => { setTemplatesDialogOpen(false); resetTemplateForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>{t('edukacijos.manageTemplates', 'Manage templates')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <TextField label={t('edukacijos.templateName', 'Template name')} value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} fullWidth />
            <TextField label={t('edukacijos.titleLabel', 'Title')} value={templateForm.title} onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })} fullWidth />
            <TextField label={t('edukacijos.description', 'Description')} value={templateForm.description} onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} multiline minRows={2} fullWidth />
            <TextField label={t('edukacijos.location', 'Location')} value={templateForm.location} onChange={(e) => setTemplateForm({ ...templateForm, location: e.target.value })} fullWidth />
            <TextField label={t('edukacijos.startTime', 'Start Time')} value={templateForm.startTime} onChange={(e) => setTemplateForm({ ...templateForm, startTime: e.target.value })} fullWidth />
            <TextField label={t('edukacijos.endTime', 'End Time')} value={templateForm.endTime} onChange={(e) => setTemplateForm({ ...templateForm, endTime: e.target.value })} fullWidth />
            <TextField label={t('edukacijos.maxParticipants', 'Max Participants')} type="number" value={templateForm.maxParticipants} onChange={(e) => setTemplateForm({ ...templateForm, maxParticipants: parseInt(e.target.value) || 0 })} fullWidth />
            <TextField label={t('edukacijos.notes', 'Notes')} value={templateForm.notes} onChange={(e) => setTemplateForm({ ...templateForm, notes: e.target.value })} multiline minRows={2} fullWidth />
            <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('edukacijos.assignColleaguesHeading', 'Priskirti kolegos')}
              </Typography>
              {(templateForm.assignedColleagueIds || ['']).map((colleagueId, assigneeIndex) => (
                <Box key={assigneeIndex} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel id={`tmpl-eduk-assign-${assigneeIndex}`}>
                      {t('edukacijos.assignColleague', 'Assign Colleague')} {assigneeIndex + 1}
                    </InputLabel>
                    <Select
                      labelId={`tmpl-eduk-assign-${assigneeIndex}`}
                      value={colleagueId}
                      onChange={(e) => setTemplateAssigneeAt(assigneeIndex, e.target.value)}
                      label={`${t('edukacijos.assignColleague', 'Assign Colleague')} ${assigneeIndex + 1}`}
                    >
                      <MenuItem value="">
                        <em>{t('edukacijos.noAssignment', 'No assignment')}</em>
                      </MenuItem>
                      {colleagues.map((colleague) => (
                        <MenuItem key={colleague.id} value={colleague.id}>
                          {colleague.firstName} {colleague.lastName}
                          {colleague.department && ` (${colleague.department})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(templateForm.assignedColleagueIds || ['']).length > 1 && (
                    <Tooltip title={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}>
                      <IconButton
                        aria-label={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}
                        onClick={() => removeTemplateAssigneeRow(assigneeIndex)}
                        sx={{ mt: 0.5 }}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
              {(templateForm.assignedColleagueIds || ['']).length +
                (templateForm.watcherColleagueIds || ['']).length <
                MAX_EDUKATION_FORM_ASSIGNEES && (
                <Tooltip title={t('edukacijos.addAnotherColleague', 'Add another colleague')}>
                  <IconButton
                    color="primary"
                    aria-label={t('edukacijos.addAnotherColleague', 'Add another colleague')}
                    onClick={addTemplateAssigneeRow}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('edukacijos.watchersHeading', 'Stebėtojai')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t(
                  'edukacijos.watchersHint',
                  'Matomi darbo grafike, bet neįtraukiami į veiklos ataskaitą.'
                )}
              </Typography>
              {(templateForm.watcherColleagueIds || ['']).map((colleagueId, watcherIndex) => (
                <Box key={watcherIndex} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel id={`tmpl-eduk-watch-${watcherIndex}`}>
                      {t('edukacijos.assignWatcher', 'Stebėtojas')} {watcherIndex + 1}
                    </InputLabel>
                    <Select
                      labelId={`tmpl-eduk-watch-${watcherIndex}`}
                      value={colleagueId}
                      onChange={(e) => setTemplateWatcherAt(watcherIndex, e.target.value)}
                      label={`${t('edukacijos.assignWatcher', 'Stebėtojas')} ${watcherIndex + 1}`}
                    >
                      <MenuItem value="">
                        <em>{t('edukacijos.noAssignment', 'No assignment')}</em>
                      </MenuItem>
                      {colleagues.map((colleague) => (
                        <MenuItem key={colleague.id} value={colleague.id}>
                          {colleague.firstName} {colleague.lastName}
                          {colleague.department && ` (${colleague.department})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(templateForm.watcherColleagueIds || ['']).length > 1 && (
                    <Tooltip title={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}>
                      <IconButton
                        aria-label={t('edukacijos.removeAssigneeSlot', 'Remove this slot')}
                        onClick={() => removeTemplateWatcherRow(watcherIndex)}
                        sx={{ mt: 0.5 }}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
              {(templateForm.assignedColleagueIds || ['']).length +
                (templateForm.watcherColleagueIds || ['']).length <
                MAX_EDUKATION_FORM_ASSIGNEES && (
                <Tooltip title={t('edukacijos.addAnotherWatcher', 'Pridėti stebėtoją')}>
                  <IconButton
                    color="primary"
                    aria-label={t('edukacijos.addAnotherWatcher', 'Pridėti stebėtoją')}
                    onClick={addTemplateWatcherRow}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={saveTemplate}>
              {editingTemplateId ? t('edukacijos.updateTemplate', 'Update template') : t('edukacijos.saveTemplate', 'Save template')}
            </Button>
            {editingTemplateId && (
              <Button onClick={resetTemplateForm}>{t('common.cancel', 'Cancel')}</Button>
            )}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {templates.map((template) => (
              <Card key={template.id} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2">{template.name}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{template.title}</Typography>
                      {template.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '2.4em' }}>
                          {template.description.length > 80 ? `${template.description.slice(0, 80)}…` : template.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={() => editTemplate(template)}>{t('common.edit', 'Edit')}</Button>
                      <Button size="small" color="error" onClick={() => deleteTemplate(template.id)}>{t('common.delete', 'Delete')}</Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
            {templates.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('edukacijos.noTemplates', 'No templates yet')}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTemplatesDialogOpen(false); resetTemplateForm(); }}>
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      <ExhibitionDeletePasswordDialog
        open={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onVerified={async () => {
          if (deleteTargetId) {
            await performDeleteEdukation(deleteTargetId);
          }
        }}
      />

      <Dialog
        open={unconfirmTargetId !== null}
        onClose={() => !unconfirmingId && setUnconfirmTargetId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('edukacijos.unconfirmParticipantsTitle', 'Atšaukti skaičiaus patvirtinimą?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              'edukacijos.unconfirmParticipantsHelp',
              'Skaičius vėl bus nepatvirtintas. Edukaciją galėsite patvirtinti vėliau (prieš užbaigiant).'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnconfirmTargetId(null)} disabled={!!unconfirmingId}>
            {t('common.cancel', 'Atšaukti')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={!!unconfirmingId || !unconfirmTargetId}
            onClick={() => {
              if (unconfirmTargetId) {
                void handleUnconfirmParticipants(unconfirmTargetId);
              }
            }}
          >
            {unconfirmingId ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('edukacijos.unconfirmParticipants', 'Atšaukti patvirtinimą')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Edukacijos;
